/**
 * BuildMode — the creation half of the loop.
 *
 * The design target is *not* a level editor. It is the shortest path from
 * "someone fell for that" to "try this instead". So:
 *  - the question the UI asks is never "design a level", it is "他会怎么走？"
 *    — the palette copy is written about the person who will walk in;
 *  - every prop is one tap, and every mistake is one undo;
 *  - illegal placements are refused *while your finger is down*, not on
 *    publish, so nobody builds for two minutes to be told no;
 *  - 试玩 is next to 发布, because the only way to know if a bluff works is to
 *    walk into it yourself.
 */

import { PROP_ORDER, propType } from '../core/props'
import type { PropTypeId } from '../core/props'
import type { PropInstance, RoomDef, Rot } from '../core/room'
import {
  ROOM_CONFIG,
  cellDistance,
  cellKey,
  cloneRoom,
  nextIid,
  spentBudget,
  worldToCell,
} from '../core/room'
import { canPlace, checkRoom } from '../core/validate'
import { RoomView } from '../render/roomView'
import type { SceneView } from '../render/scene'
import { button, el, toast } from '../ui/dom'

type Tool = { kind: 'prop'; type: PropTypeId } | { kind: 'chest' } | { kind: 'inspect' }

export interface BuildModeDeps {
  scene: SceneView
  uiHost: HTMLElement
  room: RoomDef
  onPlaytest: (room: RoomDef) => void
  onPublish: (room: RoomDef) => void
  onExit: () => void
}

const MAX_HISTORY = 40

export class BuildMode {
  private view: RoomView
  private room: RoomDef
  private tool: Tool = { kind: 'prop', type: 'brick' }
  private selected: PropInstance | null = null
  private undoStack: RoomDef[] = []
  private redoStack: RoomDef[] = []
  private rot: Rot = 0
  private panel: HTMLDivElement
  private budgetBar!: HTMLDivElement
  private budgetLabel!: HTMLSpanElement
  private hintLabel!: HTMLDivElement
  private paletteEl!: HTMLDivElement
  private actionsEl!: HTMLDivElement
  private dragging: PropInstance | null = null
  private pointerDownAt = { x: 0, y: 0, t: 0 }
  private canvas: HTMLCanvasElement

  constructor(private deps: BuildModeDeps) {
    this.room = cloneRoom(deps.room)
    this.view = new RoomView(this.room)
    this.view.setGridVisible(true)
    deps.scene.scene.add(this.view.root)
    deps.scene.setRoomSize(this.room.width, this.room.depth)
    deps.scene.setMode('overview')

    this.canvas = deps.scene.renderer.domElement
    this.canvas.addEventListener('pointerdown', this.onPointerDown)
    this.canvas.addEventListener('pointermove', this.onPointerMove)
    this.canvas.addEventListener('pointerup', this.onPointerUp)
    this.canvas.addEventListener('pointerleave', this.onPointerLeave)

    this.panel = el('div', { class: 'build-ui' })
    deps.uiHost.appendChild(this.panel)
    this.renderChrome()
    this.refresh()
  }

  // --------------------------------------------------------------------- chrome

  private renderChrome(): void {
    const top = el('div', { class: 'build-top' }, [
      button('← 返回', () => this.deps.onExit(), 'btn ghost'),
      el('div', { class: 'build-title', text: '布置你的屋子' }),
      el('div', { class: 'build-top-right' }, [
        button('↶', () => this.undo(), 'btn icon'),
        button('↷', () => this.redo(), 'btn icon'),
      ]),
    ])

    this.budgetLabel = el('span', { class: 'budget-label' })
    this.budgetBar = el('div', { class: 'budget-fill' })
    const budget = el('div', { class: 'budget' }, [
      el('div', { class: 'budget-track' }, [this.budgetBar]),
      this.budgetLabel,
    ])

    this.hintLabel = el('div', { class: 'build-hint' })
    this.actionsEl = el('div', { class: 'selected-actions' })
    this.paletteEl = el('div', { class: 'palette' })

    const bottom = el('div', { class: 'build-bottom' }, [
      this.hintLabel,
      this.actionsEl,
      this.paletteEl,
      el('div', { class: 'build-cta' }, [
        button('留一句话', () => this.editTaunt(), 'btn ghost'),
        button('试玩', () => this.playtest(), 'btn'),
        button('发布并复制链接', () => this.publish(), 'btn primary'),
      ]),
    ])

    this.panel.append(top, budget, bottom)
    this.renderPalette()
  }

  private renderPalette(): void {
    this.paletteEl.textContent = ''
    const chest = el(
      'button',
      {
        class: `chip${this.tool.kind === 'chest' ? ' chip-on' : ''}`,
        type: 'button',
        onclick: () => {
          this.tool = { kind: 'chest' }
          this.selected = null
          this.refresh()
        },
      },
      [
        el('span', { class: 'chip-name', text: '宝箱' }),
        el('span', { class: 'chip-cost', text: '移动' }),
      ],
    )
    this.paletteEl.appendChild(chest)

    for (const id of PROP_ORDER) {
      const t = propType(id)
      const on = this.tool.kind === 'prop' && this.tool.type === id
      const chip = el(
        'button',
        {
          class: `chip${on ? ' chip-on' : ''}`,
          type: 'button',
          onclick: () => {
            this.tool = { kind: 'prop', type: id }
            this.selected = null
            this.refresh()
          },
        },
        [
          el('span', { class: 'chip-swatch', style: `background:#${t.color.toString(16).padStart(6, '0')}` }),
          el('span', { class: 'chip-name', text: t.name }),
          el('span', { class: 'chip-cost', text: `${t.cost}` }),
        ],
      )
      this.paletteEl.appendChild(chip)
    }
  }

  private refresh(): void {
    const spent = spentBudget(this.room.props)
    const pct = Math.min(100, (spent / ROOM_CONFIG.budget) * 100)
    this.budgetBar.style.width = `${pct}%`
    this.budgetBar.classList.toggle('over', spent > ROOM_CONFIG.budget)
    this.budgetLabel.textContent = `预算 ${spent}/${ROOM_CONFIG.budget}`

    if (this.selected) {
      const t = propType(this.selected.type)
      this.hintLabel.textContent = t.hint
      this.renderSelectedActions()
    } else if (this.tool.kind === 'prop') {
      this.hintLabel.textContent = propType(this.tool.type).hint
      this.actionsEl.textContent = ''
    } else if (this.tool.kind === 'chest') {
      this.hintLabel.textContent = `点一格移动真宝箱。离门至少 ${ROOM_CONFIG.minChestDistance} 格。`
      this.actionsEl.textContent = ''
    } else {
      this.hintLabel.textContent = ''
      this.actionsEl.textContent = ''
    }

    this.renderPalette()
    this.view.setSelection(this.selected ? { x: this.selected.x, z: this.selected.z } : null)
  }

  private renderSelectedActions(): void {
    this.actionsEl.textContent = ''
    const sel = this.selected
    if (!sel) return
    const t = propType(sel.type)
    const actions: HTMLElement[] = []
    if (t.directional) {
      actions.push(
        button('旋转', () => {
          this.commit((room) => {
            const p = room.props.find((x) => x.iid === sel.iid)
            if (p) p.rot = (((p.rot + 1) % 4) as Rot)
          })
          this.selected = this.room.props.find((p) => p.iid === sel.iid) ?? null
        }),
      )
    }
    actions.push(
      button('复制', () => this.duplicate(sel)),
      button('删除', () => this.remove(sel), 'btn danger'),
    )
    for (const a of actions) this.actionsEl.appendChild(a)
  }

  // ---------------------------------------------------------------- edit model

  /** Every mutation goes through here, so undo is never partial. */
  private commit(mutate: (room: RoomDef) => void): void {
    this.undoStack.push(cloneRoom(this.room))
    if (this.undoStack.length > MAX_HISTORY) this.undoStack.shift()
    this.redoStack = []
    const next = cloneRoom(this.room)
    mutate(next)
    this.room = next
    this.view.rebuild(this.room)
    this.view.setGridVisible(true)
    this.refresh()
  }

  private undo(): void {
    const prev = this.undoStack.pop()
    if (!prev) return
    this.redoStack.push(cloneRoom(this.room))
    this.room = prev
    this.selected = null
    this.view.rebuild(this.room)
    this.view.setGridVisible(true)
    this.refresh()
  }

  private redo(): void {
    const next = this.redoStack.pop()
    if (!next) return
    this.undoStack.push(cloneRoom(this.room))
    this.room = next
    this.selected = null
    this.view.rebuild(this.room)
    this.view.setGridVisible(true)
    this.refresh()
  }

  private place(type: PropTypeId, x: number, z: number): void {
    const verdict = canPlace(this.room, type, x, z)
    if (!verdict.ok) {
      toast(this.deps.uiHost, verdict.reason ?? '放不了')
      return
    }
    this.commit((room) => {
      room.props.push({ iid: nextIid(room), type, x, z, rot: this.rot })
    })
  }

  private duplicate(sel: PropInstance): void {
    // Drop the copy on the first free neighbouring cell — one tap, no aiming.
    const offsets = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, 1],
      [-1, -1],
    ] as const
    for (const [dx, dz] of offsets) {
      const x = sel.x + dx
      const z = sel.z + dz
      if (canPlace(this.room, sel.type, x, z).ok) {
        this.commit((room) => {
          room.props.push({ iid: nextIid(room), type: sel.type, x, z, rot: sel.rot })
        })
        return
      }
    }
    toast(this.deps.uiHost, '旁边没地方了')
  }

  private remove(sel: PropInstance): void {
    this.commit((room) => {
      room.props = room.props.filter((p) => p.iid !== sel.iid)
    })
    this.selected = null
    this.refresh()
  }

  private moveTo(sel: PropInstance, x: number, z: number): void {
    if (sel.x === x && sel.z === z) return
    // Legality is checked against the room *without* this prop, otherwise a
    // prop can never move one cell (it collides with its own old position).
    const without: RoomDef = { ...this.room, props: this.room.props.filter((p) => p.iid !== sel.iid) }
    const verdict = canPlace(without, sel.type, x, z)
    if (!verdict.ok) {
      toast(this.deps.uiHost, verdict.reason ?? '放不了')
      return
    }
    this.commit((room) => {
      const p = room.props.find((q) => q.iid === sel.iid)
      if (p) {
        p.x = x
        p.z = z
      }
    })
    this.selected = this.room.props.find((p) => p.iid === sel.iid) ?? null
    this.refresh()
  }

  private moveChest(x: number, z: number): void {
    if (this.room.props.some((p) => p.x === x && p.z === z)) {
      toast(this.deps.uiHost, '这格有玩具')
      return
    }
    if (cellDistance(this.room.door, { x, z }) < ROOM_CONFIG.minChestDistance) {
      toast(this.deps.uiHost, `离门太近了，至少 ${ROOM_CONFIG.minChestDistance} 格`)
      return
    }
    this.commit((room) => {
      room.chest = { x, z }
    })
  }

  // ------------------------------------------------------------------- pointer

  private cellAt(clientX: number, clientY: number): { x: number; z: number } | null {
    const hit = this.deps.scene.pickGroundPoint(clientX, clientY)
    if (!hit) return null
    const cell = worldToCell(hit.x, hit.z, this.room.width, this.room.depth)
    if (cell.x < 0 || cell.x >= this.room.width || cell.z < 0 || cell.z >= this.room.depth) return null
    return cell
  }

  private propAt(cell: { x: number; z: number }): PropInstance | null {
    const key = cellKey(cell.x, cell.z)
    return this.room.props.find((p) => cellKey(p.x, p.z) === key) ?? null
  }

  private onPointerDown = (e: PointerEvent): void => {
    this.pointerDownAt = { x: e.clientX, y: e.clientY, t: performance.now() }
    const cell = this.cellAt(e.clientX, e.clientY)
    if (!cell) return
    const hit = this.propAt(cell)
    if (hit) {
      this.dragging = hit
      this.selected = hit
      this.tool = { kind: 'inspect' }
      this.refresh()
    }
  }

  private onPointerMove = (e: PointerEvent): void => {
    const cell = this.cellAt(e.clientX, e.clientY)
    if (!cell) {
      this.view.setPreview(null, 0, 0, 0, false)
      return
    }
    if (this.dragging) {
      const sel = this.dragging
      const without: RoomDef = {
        ...this.room,
        props: this.room.props.filter((p) => p.iid !== sel.iid),
      }
      const ok = canPlace(without, sel.type, cell.x, cell.z).ok || (sel.x === cell.x && sel.z === cell.z)
      this.view.setPreview(sel.type, cell.x, cell.z, sel.rot, ok)
      return
    }
    if (this.tool.kind === 'prop') {
      const ok = canPlace(this.room, this.tool.type, cell.x, cell.z).ok
      this.view.setPreview(this.tool.type, cell.x, cell.z, this.rot, ok)
    }
  }

  private onPointerUp = (e: PointerEvent): void => {
    const cell = this.cellAt(e.clientX, e.clientY)
    this.view.setPreview(null, 0, 0, 0, false)
    const moved = Math.hypot(e.clientX - this.pointerDownAt.x, e.clientY - this.pointerDownAt.y) > 12
    const dragged = this.dragging
    this.dragging = null

    if (!cell) return

    if (dragged) {
      if (moved) this.moveTo(dragged, cell.x, cell.z)
      return
    }
    if (moved) return // a drag on empty floor is a camera gesture, not a placement

    if (this.tool.kind === 'chest') {
      this.moveChest(cell.x, cell.z)
      return
    }
    if (this.tool.kind === 'prop') {
      this.place(this.tool.type, cell.x, cell.z)
    }
  }

  private onPointerLeave = (): void => {
    this.view.setPreview(null, 0, 0, 0, false)
  }

  // ---------------------------------------------------------------- taunt / go

  private editTaunt(): void {
    const current = this.room.taunt
    const next = window.prompt(
      `留一句挑衅（最多 ${ROOM_CONFIG.maxTauntLength} 字）。这是最便宜的机关。`,
      current,
    )
    if (next === null) return
    this.commit((room) => {
      room.taunt = next.trim().slice(0, ROOM_CONFIG.maxTauntLength)
    })
  }

  private validated(): RoomDef | null {
    const result = checkRoom(this.room)
    if (!result.ok) {
      toast(this.deps.uiHost, result.errors[0]?.message ?? '这间屋子还不能发布', 3000)
      return null
    }
    if (result.warnings.length > 0) {
      toast(this.deps.uiHost, result.warnings[0]!.message, 3000)
    }
    return this.room
  }

  private playtest(): void {
    const room = this.validated()
    if (room) this.deps.onPlaytest(cloneRoom(room))
  }

  private publish(): void {
    const room = this.validated()
    if (room) this.deps.onPublish(cloneRoom(room))
  }

  // ------------------------------------------------------------------ lifecycle

  update(time: number, dt: number): void {
    this.view.update(time)
    this.deps.scene.update(dt, null)
  }

  get currentRoom(): RoomDef {
    return this.room
  }

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown)
    this.canvas.removeEventListener('pointermove', this.onPointerMove)
    this.canvas.removeEventListener('pointerup', this.onPointerUp)
    this.canvas.removeEventListener('pointerleave', this.onPointerLeave)
    this.view.dispose()
    this.panel.remove()
  }
}
